import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import BookingGetters from "../../store/onlineBooking/getters";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxFooter from "./components/ShareboxFooter";

type Props = {
  token: string;
  movementId?: string;
  email: string;
  pickup: ShareboxPickupResponse;
  onBack?: () => void;
  onDone?: () => void;
};

// Placeholder contract summary — there is no contract number/page-count/PDF
// URL anywhere in the API yet, so this card is static demo content (same
// placeholder approach agreed for D8's QR code and D9's parking grid).
const DEMO_CONTRACT_NUMBER = "DG 1234567";
const DEMO_PAGE_COUNT = 2;

const MAX_CAR_PHOTOS = 4;

export default function D10ContractEmail({
  token,
  movementId,
  email,
  pickup,
  onBack,
  onDone,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const renterName = pickup.booking.renterName;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [carPhotos, setCarPhotos] = useState<File[]>([]);
  const [carPhotoUrls, setCarPhotoUrls] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const [contractUrl, setContractUrl] = useState<string | null>(null);
  const contractFetchedRef = useRef(false);

  useEffect(() => {
    if (contractFetchedRef.current || !token) return;
    contractFetchedRef.current = true;
    setDownloadingContract(true);
    BookingApi.getShareboxSignedContractUrls(token)
      .then((response) => {
        const urls = response.data.urls;
        setContractUrl(urls?.[1] || urls?.[0] || null);
      })
      .catch((error) => {
        console.error("Failed to pre-fetch the signed contract:", error);
      })
      .finally(() => setDownloadingContract(false));
  }, [token]);

  const handleDownloadContract = async () => {
    if (!contractUrl) return;
    window.open(contractUrl, "_blank", "noopener,noreferrer");
    try {
      const pdfResponse = await fetch(contractUrl);
      const blob = await pdfResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `contract-${pickup.reservationCode || "signed"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (blobErr) {
      console.error(
        "Sharebox signed contract: blob-fetch download failed (new tab still opened)",
        blobErr
      );
    }
  };

  useEffect(() => {
    const objectUrls = carPhotos.map((file) => URL.createObjectURL(file));
    setCarPhotoUrls(objectUrls);
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [carPhotos]);

  const handleCarPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) {
      setCarPhotos((prev) => [...prev, ...selected].slice(0, MAX_CAR_PHOTOS));
    }
    e.target.value = "";
  };

  const handleDone = async () => {
    if (carPhotos.length && movementId) {
      setFinishing(true);
      for (const photo of carPhotos) {
        try {
          await BookingApi.uploadShareboxCarPhoto(token, photo, movementId);
        } catch (error) {
          console.error("Failed to upload a car photo:", error);
        }
      }
      setFinishing(false);
    }
    if (onDone) onDone();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={`${translate("Step")} 3 ${translate("of")} 3`}
        onBack={onBack}
      />

      <Box sx={{ maxWidth: 480, mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {translate("Your rental is active")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate("Drive safe")}, {renterName}.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "action.hover",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: webCheckinSecondaryColor || "#E0007A",
              borderRadius: 1,
            }}
          >
            <PictureAsPdfIcon
              sx={{
                color: webCheckinSecondaryColor || "#E0007A",
                fontSize: 20,
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>
              {translate("Contract")} {DEMO_CONTRACT_NUMBER}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate("Signed via OTP")} · {DEMO_PAGE_COUNT}{" "}
              {translate("pages")}
            </Typography>
          </Box>
          <IconButton
            size="small"
            disabled={downloadingContract}
            onClick={handleDownloadContract}
            sx={{ color: webCheckinSecondaryColor || "#E0007A" }}
          >
            {downloadingContract ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              <ArrowDownwardIcon />
            )}
          </IconButton>
        </Box>

        <Box
          sx={{
            backgroundColor: (theme) =>
              alpha(webCheckinSecondaryColor || theme.palette.error.main, 0.08),
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: webCheckinSecondaryColor || "#E0007A",
              mb: 0.5,
            }}
          >
            {translate("Add a photo of the car (optional)")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {translate(
              "Recommended before you leave — it protects you from pre-existing damage disputes."
            )}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleCarPhotoChange}
          />
          <Button
            variant="outlined"
            disabled={carPhotos.length >= MAX_CAR_PHOTOS}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              borderColor: webCheckinSecondaryColor || "#E0007A",
              color: webCheckinSecondaryColor || "#E0007A",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              "&:hover": {
                borderColor: webCheckinSecondaryColor || "#E0007A",
                backgroundColor: "transparent",
              },
            }}
          >
            {carPhotos.length
              ? `${translate("Upload photo")} (${
                  carPhotos.length
                }/${MAX_CAR_PHOTOS})`
              : translate("Upload photo")}
          </Button>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 1,
              mt: 1.5,
            }}
          >
            {Array.from({ length: MAX_CAR_PHOTOS }).map((_, index) => {
              const url = carPhotoUrls[index];
              return url ? (
                <Box
                  key={index}
                  component="button"
                  type="button"
                  onClick={() => setPreviewIndex(index)}
                  sx={{
                    p: 0,
                    border: "none",
                    borderRadius: "8px",
                    overflow: "hidden",
                    cursor: "pointer",
                    aspectRatio: "1 / 1",
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={`${translate("Car photo")} ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  key={index}
                  sx={{
                    aspectRatio: "1 / 1",
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddAPhotoOutlinedIcon
                    sx={{ color: "text.disabled", fontSize: 20 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        <Button
          variant="contained"
          fullWidth
          className="webCheckInButton"
          disabled={finishing}
          onClick={handleDone}
          sx={{
            width: "100% !important",
            maxWidth: "none !important",
            color: "#fff",
          }}
        >
          {finishing ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            translate("Done")
          )}
        </Button>
      </Box>
      <ShareboxFooter />

      <Dialog
        open={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ position: "relative", backgroundColor: "#000" }}>
          <IconButton
            onClick={() => setPreviewIndex(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.4)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewIndex !== null && carPhotoUrls[previewIndex] && (
            <Box
              component="img"
              src={carPhotoUrls[previewIndex]}
              alt={`${translate("Car photo")} ${previewIndex + 1}`}
              sx={{
                display: "block",
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
